import { memo, type ReactElement, type ReactNode } from "react";

import { HtmlContent } from "@vega-core/components/HtmlContent/HtmlContent";
import { Badge } from "@vega-core/components/shadcn/badge";
import { TooltipInfoIcon } from "@vega-core/components/Tooltip/TooltipInfoIcon";
import { m } from "@vega-core/paraglide/messages";

import { cnTwMerge } from "../../../utils/cnTwMerge";
import { isTruthy } from "../../../utils/isTruthy";

type Props = {
  className?: string;
  compact?: boolean;
  forName?: string;
  hint?: ReactElement | string;
  isOptional?: boolean;
  label?: ReactNode | string | null;
  labelClassName?: string;
  labelElement?: ReactElement | string | null;
  labelHint?: ReactNode | string | null;
  tooltipContent?: string;
  tag?: ReactNode;
};

export const FormLabel = memo(
  ({
    className,
    compact = false,
    forName,
    hint,
    isOptional,
    label,
    labelClassName,
    labelElement,
    labelHint,
    tooltipContent,
    tag,
  }: Props) => (
    <div
      className={cnTwMerge(
        "flex items-center justify-between w-full text-text/primary",
        {
          [`${compact ? "mb-0.5" : "mb-1"}`]:
            isTruthy(label) ||
            isTruthy(hint) ||
            isTruthy(isOptional) ||
            isTruthy(labelElement),
        },
        className,
      )}
    >
      <label
        aria-label="form-label"
        className={cnTwMerge(
          "flex flex-wrap items-start",
          compact ? "body-extra-small-medium" : "body-base-medium",
          labelClassName,
        )}
        htmlFor={forName}
      >
        <span className="inline">
          {label ? <HtmlContent content={label} /> : labelElement}
          {tooltipContent ? (
            <TooltipInfoIcon
              className="inline-flex w-5 h-5 ml-1"
              content={tooltipContent}
            />
          ) : null}
        </span>
        {labelHint ? (
          <span className="ml-1 body-small-regular text-text/secondary">
            {labelHint}
          </span>
        ) : null}
      </label>
      <span className="flex items-center ml-auto body-small-medium gap-2 self-start">
        {hint ? <span className="text-text/disabled">{hint}</span> : null}
        {isTruthy(isOptional) ? (
          <span
            className={cnTwMerge(
              "ml-1 text-text/disabled mt-0.5",
              compact ? "body-extra-small-medium" : "body-small-medium",
            )}
          >
            ({m.form_optional()})
          </span>
        ) : null}
        {tag ? (
          <Badge className="px-1 body-extra-small-large bg-background/component/10 rounded-small cursor-auto">
            {tag}
          </Badge>
        ) : null}
      </span>
    </div>
  ),
);
